import { Vector3 } from '../types/Vector3'
import { Quaternion } from '../types/Quaternion'
import { FieldContext } from './FieldContext'
import { FieldTransform, FieldAnimationState, FieldPhaseData, HoverDirection, RotationMode, UpVectorMode, AlignmentMode } from './FieldTypes'
import { PhaseUtils } from '../../math/PhaseUtils'
import { VectorUtils } from '../../math/VectorUtils'
import { IndexTransforms } from '../../math/IndexTransforms'

export class FieldAwareness {
  // ==================== CORE PROPERTIES ====================
  
  private _context: FieldContext
  private _lastUpdateTime: number = 0
  private _hoverDisabledLogged: boolean = false
  private _lastLogTime: number = 0 // Add throttling for logging
  
  // ==================== CONSTRUCTOR ====================
  
  constructor(context: FieldContext) {
    this._context = context
  }
  
  // ==================== MAIN UPDATE METHOD ====================
  
  /**
   * Main update method called every frame for each POI
   */
  public update(poi: any, deltaTime: number): void {
    // Update time
    this._lastUpdateTime += deltaTime
    this._context.updateTime(deltaTime)
    
    // DEBUG: Log that field awareness is running (only for POI 0, every 60 seconds)
    if (poi.index === 0 && Math.floor(this._lastUpdateTime) % 60 === 0) {
      console.log('🌪️ FIELD AWARENESS IS RUNNING for POI 0')
    }
    
    // Calculate field transform
    const fieldTransform = this.computeFieldTransform(poi.index)
    
    // Calculate local animations
    const animations = this.computeLocalAnimations(poi, deltaTime)
    
    // Apply combined transform
    this.applyTransform(poi, fieldTransform, animations)
  }
  
  // ==================== FIELD TRANSFORM CALCULATION ====================
  
  /**
   * Calculate position and orientation from field
   */
  public computeFieldTransform(index: number): FieldTransform {
    const layout = this._context.getLayoutInstance()
    const params = this._context.parameters
    
    // Get base position and orientation from layout
    const position = layout.computePosition(index, this._context.totalCount, params)
    const rotation = layout.computeOrientation(index, this._context.totalCount, params)
    
    // DEBUG: Log orientation calculation only for POI 0 to avoid spam - REDUCED FREQUENCY
    if (index === 0 && Math.floor(this._lastUpdateTime) % 30 === 0) {
      //console.log(`🔄 FieldTransform for POI ${index}:`)
      //console.log(`  Layout type: ${this._context.layoutType}`)
      //console.log(`  Alignment mode: ${params.alignmentMode}`)
      //console.log(`  Position:`, position)
      //console.log(`  Rotation:`, rotation)
      //console.log(`  Rotation (Euler):`, this.quaternionToEulerDegrees(rotation))
      //console.log(`  Field direction:`, params.fieldDirection)
    }
    
    return { position, rotation }
  }
  
  /**
   * Convert quaternion to readable Euler angles in degrees
   */
  private quaternionToEulerDegrees(quat: Quaternion): { x: number, y: number, z: number } {
    const { x, y, z, w } = quat
    
    // Convert to Euler angles (ZYX order)
    const sinr_cosp = 2 * (w * x + y * z)
    const cosr_cosp = 1 - 2 * (x * x + y * y)
    const roll = Math.atan2(sinr_cosp, cosr_cosp)
    
    const sinp = 2 * (w * y - z * x)
    const pitch = Math.abs(sinp) >= 1 ? Math.sign(sinp) * Math.PI / 2 : Math.asin(sinp)
    
    const siny_cosp = 2 * (w * z + x * y)
    const cosy_cosp = 1 - 2 * (y * y + z * z)
    const yaw = Math.atan2(siny_cosp, cosy_cosp)
    
    // Convert to degrees
    return {
      x: roll * 180 / Math.PI,
      y: pitch * 180 / Math.PI, 
      z: yaw * 180 / Math.PI
    }
  }
  
  // ==================== PHASE CALCULATION ====================
  
  /**
   * Calculate phase data for animations
   */
  public computePhase(index: number): FieldPhaseData {
    const params = this._context.parameters
    
    let phases: { hoverPhase: number; rotatePhase: number }
    
    if (this._context.is1D()) {
      // 1D field phase calculation
      phases = PhaseUtils.computeDualPhases(
        index,
        this._context.totalCount,
        params.hoverPhaseShift,
        params.rotatePhaseShift
      )
    } else {
      // 2D field phase calculation
      const coords = this.get2DCoordinates(index)
      phases = PhaseUtils.computeDualPhases2D(
        coords.row,
        coords.col,
        this.getMaxDimension(),
        params.hoverPhaseShift,
        params.rotatePhaseShift
      )
    }
    
    return {
      hoverPhase: phases.hoverPhase,
      rotatePhase: phases.rotatePhase,
      index,
      totalCount: this._context.totalCount
    }
  }
  
  // ==================== LOCAL ANIMATIONS ====================
  
  /**
   * Calculate local animations (hover, rotate, scale)
   */
  public computeLocalAnimations(poi: any, deltaTime: number): FieldAnimationState {
    const phaseData = this.computePhase(poi.index)
    const params = this._context.parameters
    
    // Calculate hover animation
    const hoverOffset = this.calculateHoverAnimation(poi.index, phaseData)
    
    // ⚠️ RE-ENABLED: Rotation animation to show alignment differences
    const rotateOffset = this.calculateRotationAnimation(poi.index, phaseData)
    
    // DISABLED: Scale animation - deaktiviert fürs erste  
    const scaleOffset = 1.0 // No scaling
    
    return {
      hoverOffset,
      rotateOffset,
      scaleOffset
    }
  }
  
  // ==================== HOVER ANIMATION ====================
  
  /**
   * Calculate hover animation offset
   */
  private calculateHoverAnimation(index: number, phaseData: FieldPhaseData): Vector3 {
    const params = this._context.parameters
    const time = params.time
    
    if (params.hoverAmplitude === 0) {
      return new Vector3(0, 0, 0)
    }
    
    // Get hover direction (now properly uses alignment mode)
    const hoverDirection = this.getHoverDirection(index)
    
    // Calculate hover wave
    const hoverWave = PhaseUtils.applyWave(
      params.hoverAmplitude,
      params.hoverFrequency,
      time,
      phaseData.hoverPhase
    )
    
    // Apply hover wave to direction
    return new Vector3(
      hoverDirection.x * hoverWave,
      hoverDirection.y * hoverWave,
      hoverDirection.z * hoverWave
    )
  }
  
  /**
   * Get hover direction vector based on hover direction mode and alignment mode
   */
  private getHoverDirection(index: number): Vector3 {
    const params = this._context.parameters
    
    // Debug log alignment mode changes for POI 0
    // Logging disabled to reduce console spam
    
    // Check for manual override first
    if (params.hoverDirectionOverride === 'vertical') {
      return new Vector3(0, 1, 0) // Always vertical
    } else if (params.hoverDirectionOverride === 'horizontal') {
      // Use a horizontal direction perpendicular to field direction
      const fieldDir = params.fieldDirection.clone().normalize()
      // If field direction is Y-axis, use X-axis, otherwise project to XZ plane
      if (Math.abs(fieldDir.y) > 0.99) {
        return new Vector3(1, 0, 0)
      } else {
        return new Vector3(fieldDir.z, 0, -fieldDir.x).normalize() // Perpendicular in XZ plane
      }
    }

    // ⚠️ IMPROVED: Use alignment mode to determine direction
    const layout = this._context.getLayoutInstance()
    
    if (params.alignmentMode === AlignmentMode.Perpendicular) {
      // PERPENDICULAR: Always use transversal (perpendicular) direction regardless of hoverDirection
      const direction = this.getTransversalDirection(index)
      return direction
    } else {
      // ALIGNED: Use the configured hover direction
      if (params.hoverDirection === HoverDirection.Transversal) {
        // Perpendicular to field direction
        const direction = this.getTransversalDirection(index)
        return direction
      } else {
        // Along field direction (Longitudinal)
        const direction = this.getLongitudinalDirection(index)
        return direction
      }
    }
  }
  
  /**
   * Get transversal (perpendicular) direction
   */
  private getTransversalDirection(index: number): Vector3 {
    const layout = this._context.getLayoutInstance()
    const params = this._context.parameters
    
    try {
      if (layout.computeTangent) {
        // For curve-based layouts, use normal
        const tangent = layout.computeTangent(index, this._context.totalCount, params)
        if (tangent && tangent.length() > 0) {
          const normal = VectorUtils.getNormal(tangent, params.customUpVector)
          if (normal && normal.length() > 0) {
            return normal
          }
        }
      } 
      
      // Fallback: For grid-based layouts or when tangent fails, use inward normal
      const position = layout.computePosition(index, this._context.totalCount, params)
      if (position && position.length() > 0) {
        const center = new Vector3() // Assume center at origin
        const inwardNormal = VectorUtils.getInwardNormal(position, center)
        if (inwardNormal && inwardNormal.length() > 0) {
          return inwardNormal
        }
      }
      
      // Ultimate fallback: Use field direction perpendicular
      const fieldDir = params.fieldDirection.clone().normalize()
      if (Math.abs(fieldDir.y) > 0.99) {
        return new Vector3(1, 0, 0) // Field is vertical, use X-axis
      } else {
        return new Vector3(0, 1, 0) // Field is horizontal, use Y-axis
      }
      
    } catch (error) {
      console.warn('Error in getTransversalDirection:', error)
      return new Vector3(0, 1, 0) // Safe fallback
    }
  }
  
  /**
   * Get longitudinal (along field) direction
   */
  private getLongitudinalDirection(index: number): Vector3 {
    const layout = this._context.getLayoutInstance()
    const params = this._context.parameters
    
    try {
      if (layout.computeTangent) {
        // For curve-based layouts, use tangent
        const tangent = layout.computeTangent(index, this._context.totalCount, params)
        if (tangent && tangent.length() > 0) {
          return tangent
        }
      }
      
      // For grid-based layouts, use direction to next position
      if (index + 1 < this._context.totalCount) {
        const currentPos = layout.computePosition(index, this._context.totalCount, params)
        const nextPos = layout.computePosition(index + 1, this._context.totalCount, params)
        if (currentPos && nextPos) {
          const direction = new Vector3(
            nextPos.x - currentPos.x,
            nextPos.y - currentPos.y,
            nextPos.z - currentPos.z
          )
          if (direction.length() > 0) {
            return direction.normalize()
          }
        }
      }
      
      // Fallback: Use field direction
      return params.fieldDirection.clone().normalize()
      
    } catch (error) {
      console.warn('Error in getLongitudinalDirection:', error)
      return params.fieldDirection.clone().normalize() // Safe fallback
    }
  }
  
  // ==================== ROTATION ANIMATION ====================
  
  /**
   * Calculate rotation animation offset
   */
  private calculateRotationAnimation(index: number, phaseData: FieldPhaseData): Quaternion {
    const params = this._context.parameters
    const time = params.time
    
    if (params.rotateAmplitude === 0) {
      return new Quaternion()  // Identity quaternion
    }
    
    // Get rotation axis
    const rotationAxis = this.getRotationAxis(index)
    
    let angle: number
    
    if (params.rotationMode === RotationMode.Infinite) {
      // Infinite continuous rotation
      // Each POI spins continuously based on frequency and phase
      angle = params.rotateFrequency * time + phaseData.rotatePhase
      // Scale by amplitude for speed control
      angle *= params.rotateAmplitude
    } else {
      // Oscillating rotation (original behavior)
      // Back-and-forth sway using sine wave
      angle = PhaseUtils.applyWave(
        params.rotateAmplitude,
        params.rotateFrequency,
        time,
        phaseData.rotatePhase
      )
    }
    
    // Create rotation quaternion
    const quaternion = new Quaternion()
    quaternion.setFromAxisAngle(rotationAxis, angle)
    
    // CRITICAL: Ensure the quaternion is normalized
    quaternion.normalize()
    
    return quaternion
  }
  
  /**
   * Get rotation axis for rotation animation
   */
  private getRotationAxis(index: number): Vector3 {
    const params = this._context.parameters
    
    // Determine rotation axis based on field parameters
    let rotationAxis: Vector3
    
    if (params.upVectorMode === UpVectorMode.WorldUp) {
      // Use world-up (constant Y-axis) for world-up mode
      rotationAxis = new Vector3(0, 1, 0)
    } else {
      // Path-up mode: use the field's local up vector
      const fieldTransform = this.computeFieldTransform(index)
      const tangent = this.getHoverDirection(index)
      const normal = this.getTransversalDirection(index)
      
      // Calculate the local up vector (perpendicular to both tangent and normal)
      rotationAxis = new Vector3().crossVectors(tangent, normal).normalize()
      
      // If cross product is zero, fallback to world up
      if (rotationAxis.length() < 0.001) {
        rotationAxis = new Vector3(0, 1, 0)
      }
    }
    
    // Apply alignment mode influence
    if (params.alignmentMode === AlignmentMode.Perpendicular) {
      // For perpendicular alignment, we might want to rotate around the field direction
      const fieldDirection = this.getLongitudinalDirection(index)
      if (fieldDirection.length() > 0.001) {
        rotationAxis = fieldDirection.normalize()
      }
    }
    
    // CRITICAL: Ensure the rotation axis is always normalized
    const normalizedAxis = rotationAxis.normalize()
    
    // SAFETY CHECK: Verify we have a valid axis
    if (normalizedAxis.length() < 0.999) {
      console.warn('⚠️ getRotationAxis: Invalid rotation axis detected!', {
        originalAxis: rotationAxis.toString(),
        normalizedAxis: normalizedAxis.toString(),
        length: normalizedAxis.length()
      });
      // Return safe fallback
      return new Vector3(0, 1, 0);
    }
    
    return normalizedAxis
  }
  
  // ==================== SCALE ANIMATION ====================
  
  /**
   * Calculate scale animation offset
   * 
   * NOTE: Scale animation is DISABLED for layout transforms.
   * Scale should only be used for special visual effects, not for basic positioning.
   * If you need scale effects (breathing, pulsing), re-enable this carefully.
   */
  private calculateScaleAnimation(index: number, phaseData: FieldPhaseData): number {
    const params = this._context.parameters
    const time = params.time
    
    // DISABLED: No scale animation for layout transforms
    // Logging disabled to reduce console spam
    return 1.0 // Always return 1.0 (no scaling) for layout positioning
    
    /*
    if (params.scaleAmplitude === 0) {
      return 1.0
    }
    
    // Calculate scale wave
    const scaleWave = PhaseUtils.applyWave(
      params.scaleAmplitude,
      params.hoverFrequency, // Use hover frequency for scale
      time,
      phaseData.hoverPhase
    )
    
    // Return scale factor (1.0 + wave)
    return 1.0 + scaleWave
    */
  }
  
  // ==================== TRANSFORM APPLICATION ====================
  
  /**
   * Apply combined transform to POI
   * 
   * NOTE: Scale is intentionally NOT applied for basic layout transforms.
   * Scale should remain constant (1,1,1) for positioning and orientation.
   * Scale animation should only be used for special effects like "breathing" or "pulsing".
   */
  private applyTransform(
    poi: any,
    fieldTransform: FieldTransform,
    animations: FieldAnimationState
  ): void {
    // Throttle logging for POI 0
    if (poi.index === 0) {
      const now = Date.now()
      if (now - this._lastLogTime > 3000) { // Log every 3 seconds
        //console.log(`🔄 FieldAwareness: Applying transform to POI ${poi.id || 'unknown'}, alignment: ${this._context.parameters.alignmentMode}`)
        //console.log(`   Field rotation:`, fieldTransform.rotation)
        //console.log(`   Hover offset:`, animations.hoverOffset)
        this._lastLogTime = now
      }
    }
    
    // Apply field position to main POI transform (but NOT rotation to avoid double rotation)
    poi.transform.position.value = fieldTransform.position
    // Don't apply rotation to POI transform - let shape handle it to avoid double rotation
    
    // Apply local hover effects to poi.shape.transform (if poi.shape exists)
    if (poi.shape && poi.shape.transform) {
      // Apply hover offset to shape position (local offset)
      poi.shape.transform.position.value = animations.hoverOffset
      
      // Apply the complete rotation (field + animation) to shape transform only
      const combinedRotation = fieldTransform.rotation.clone().multiply(animations.rotateOffset)
      
      // CRITICAL: Normalize the combined rotation to prevent scaling artifacts
      combinedRotation.normalize()
      
      poi.shape.transform.rotation.value = combinedRotation
      
      // REMOVED: Scale application for layout transforms
      // Scale should remain constant for basic layout positioning
      // Only apply scale for special animation effects, not for field layout
      // const scaleVector = new Vector3(animations.scaleOffset, animations.scaleOffset, animations.scaleOffset)
      // poi.shape.transform.scale.value = scaleVector
      
      if (poi.index === 0) {
        const now = Date.now()
        if (now - this._lastLogTime > 3000) { // Use same throttling
          //console.log(`   Applied to shape: pos=${animations.hoverOffset.x.toFixed(2)}, rot=${combinedRotation.x.toFixed(2)}, scale=unchanged`)
        }
      }
    } else {
      // Fallback: Apply everything to poi.transform if no poi.shape
      const finalPosition = new Vector3(
        fieldTransform.position.x + animations.hoverOffset.x,
        fieldTransform.position.y + animations.hoverOffset.y,
        fieldTransform.position.z + animations.hoverOffset.z
      )
      const finalRotation = fieldTransform.rotation.multiply(animations.rotateOffset)
      
      // CRITICAL: Normalize the combined rotation to prevent scaling artifacts
      finalRotation.normalize()
      
      poi.transform.position.value = finalPosition
      poi.transform.rotation.value = finalRotation
      // REMOVED: Scale application for layout transforms
      // Scale should remain constant for basic layout positioning
      // poi.transform.scale.value = new Vector3(animations.scaleOffset, animations.scaleOffset, animations.scaleOffset)
      
      if (poi.index === 0) {
        const now = Date.now()
        if (now - this._lastLogTime > 3000) { // Use same throttling
          //console.log(`   Applied to POI: pos=${finalPosition.x.toFixed(2)}, rot=${finalRotation.x.toFixed(2)}, scale=unchanged`)
        }
      }
    }
  }
  
  // ==================== UTILITY METHODS ====================
  
  /**
   * Get 2D coordinates for index
   */
  private get2DCoordinates(index: number): { row: number; col: number } {
    const params = this._context.parameters
    
    if (this._context.layoutType === 'grid') {
      return IndexTransforms.indexToGrid(index, params.gridSize)
    } else if (this._context.layoutType === 'hex') {
      const hexCoords = IndexTransforms.indexToHex(index)
      // Convert hex to approximate row/col
      return { row: hexCoords.ring, col: hexCoords.positionInRing }
    }
    
    return { row: 0, col: 0 }
  }
  
  /**
   * Get maximum dimension for 2D fields
   */
  private getMaxDimension(): number {
    const params = this._context.parameters
    
    if (this._context.layoutType === 'grid') {
      return params.gridSize
    } else if (this._context.layoutType === 'hex') {
      // Calculate max ring for hex
      const hexCoords = IndexTransforms.indexToHex(this._context.totalCount - 1)
      return hexCoords.ring
    }
    
    return 1
  }
  
  // ==================== ADVANCED EFFECTS ====================
  
  /**
   * Apply wind effect
   */
  public applyWindEffect(strength: number, direction: Vector3): void {
    const params = this._context.parameters
    params.windStrength = strength
    params.customAlignmentVector = new Vector3(direction.x, direction.y, direction.z).normalize()
  }
  
  /**
   * Apply ripple effect
   */
  public applyRippleEffect(center: Vector3, amplitude: number, frequency: number): void {
    const params = this._context.parameters
    params.hoverAmplitude = amplitude
    params.rippleFrequency = frequency
    // Additional ripple logic would go here
  }
  
  /**
   * Apply breathing effect
   */
  public applyBreathingEffect(intensity: number): void {
    const params = this._context.parameters
    params.breathingIntensity = intensity
    params.hoverDirection = HoverDirection.Transversal
    params.hoverPhaseShift = 0 // Synchronized breathing
  }
  
  // ==================== CONTEXT MANAGEMENT ====================
  
  /**
   * Update field context
   */
  public updateContext(newContext: FieldContext): void {
    this._context = newContext
  }
  
  /**
   * Get field context
   */
  public getContext(): FieldContext {
    return this._context
  }
} 